import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import CriteriaTextFieldComponent from "./TextFields/CriteriaTextFieldComponent";
import PolicyTranslationUserSelect from "./Selects/PolicyTranslationUserSelect";
import api from "../../api";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import { hasUserGroup } from "utils/shared";
import {
  POET_AVALON_POE,
  USERGROUP_TRANSLATION,
  USERGROUP_TRANSLATION_REVIEW,
} from "constants/Constants";
import { UserContext } from "context";
import PolicyTypeBanner from "components/PolicyTypeBanner";
import FirstLineContainer from "components/common/FirstLineContainer";
import Drawers from "../../components/common/Drawers";
import GreyOutButton from "components/GreyOutButton/GreyOutButton";

const TopPanelComponent = ({
  policyStateData,
  healthPlanDisplayName,
  policyNumber,
  policyTitle,
  editable,
  healthPlanKey,
  policyKey,
  handleCancelAction,
  policyVersion,
  children,
  policyData,
  setPolicyData,
  ctReviewData,
  policyHistoryData,
  setPolicyHistoryData,
  reviewData,
  setReviewData,
  resetFilter,
  updatePolicyData,
  showTranslationReviewerError = false,
  onTranslationReviewerChange,
}) => {
  const [translationReviewers, setTranslationReviewers] = useState([]);
  const [policyTranslators, setPolicyTranslators] = useState([]);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const userContext = useContext(UserContext);

  const dispatch = useDispatch();

  const hasCtReviewObjects = useMemo(
    () => Array.isArray(ctReviewData) && ctReviewData.length > 0,
    [ctReviewData]
  );

  const canOpenCtReviewDrawer = useMemo(() => {
    const normalizedStatus = (policyData.translationStatus || "")
      .trim()
      .toLowerCase();
    return (
      normalizedStatus === "assigned" ||
      normalizedStatus === "in progress" ||
      normalizedStatus === "complete" ||
      normalizedStatus === "peer review" ||
      normalizedStatus === "pdc review"
    );
  }, [policyData.translationStatus]);

  const canEditCtReviewDrawer = useMemo(() => {
    const isAdmin = userContext?.groups?.[0] === "POET-Avalon-Admin";
    const isInPoetAvalonPoe = hasUserGroup(userContext, [POET_AVALON_POE]);
    const hasCtReviewEditRole =
      userContext?.groups?.includes("POET-Avalon-Admin") ||
      (userContext?.groups?.includes("POET-Avalon-POE") &&
        (userContext?.POETSkill?.includes("Translation") ||
          userContext?.POETSkill?.includes("Manager")));
    const normalizedStatus = (policyData.translationStatus || "")
      .trim()
      .toLowerCase();
    const isEditableStatus =
      normalizedStatus === "peer review" || normalizedStatus === "pdc review";

    return (
      hasCtReviewObjects && (isAdmin || isInPoetAvalonPoe) && isEditableStatus
    );
  }, [hasCtReviewObjects, policyData.translationStatus, userContext]);

  //Populate drop downs
  useEffect(() => {
    const fetchUsers = async () => {
      dispatch(setLoading(true));

      try {
        // Fetch the list of users for the dropdowns
        const usersResponse = await api.healthPlanConfig.fetchAllUsers();

        setPolicyTranslators(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.userGroupName === POET_AVALON_POE &&
              user.poetUserAttributes.includes(USERGROUP_TRANSLATION)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );

        setTranslationReviewers(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.poetUserAttributes.includes(USERGROUP_TRANSLATION_REVIEW)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );
      } catch (e) {
        const message = formErrorStrfromObj(e) || "Could not fetch users.";
        dispatch(setDialog({ message, title: "Error" }));
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchUsers();
  }, [dispatch]);

  const handleDrawerButtonClick = (drawerType) => {
    setActiveDrawer(drawerType);
  };
  const handleTranslatorChange = (event) => {
    setPolicyData((prev) => ({
      ...prev,
      policyTranslator: event.target.value,
    }));
  };
  const handleReviewerChange = (event) => {
    setPolicyData((prev) => ({
      ...prev,
      translationReviewer: event.target.value,
    }));
    onTranslationReviewerChange?.(event.target.value);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  const groupBoxSx = {
    sx: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1,
    },
  };

  const typographyStyle = {
    component: "span",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    sx: { flex: 0.5 },
  };

  const disableAdoptionDetailsDrawer = () => {
    return policyData.overallAdoptionDecision === null;
  };

  const handleChangeControlChange = (event) => {
    setPolicyData((prevData) => ({
      ...prevData,
      policyChangeControlXrefDto: {
        ...prevData.policyChangeControlXrefDto,
        changeControlText: event.target.value,
      },
    }));
  };

  const policyTypeBannerText =
    `${healthPlanDisplayName} - ${policyVersion}` +
    (policyData.overallAdoptionDecision
      ? ` - ${policyData.overallAdoptionDecision}`
      : "");

  return (
    <>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box component="span" fontWeight="fontWeightBold">
            Policy Rules:
          </Box>{" "}
          {policyNumber} - {policyTitle}
        </Typography>
        <Typography variant="h6" sx={{ textAlign: "right", flex: 1 }}>
          {""}
        </Typography>
      </FirstLineContainer>
      <PolicyTypeBanner
        bannerText={policyTypeBannerText}
        handleCancel={handleCancelAction}
        handleDraft={() => updatePolicyData && updatePolicyData("draft", false)}
        handleReview={() =>
          updatePolicyData && updatePolicyData("review", true)
        }
        editable={editable}
      />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {/* Left Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Review Type:</Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyStateData?.cowName} {policyStateData?.quarter}
            </Typography>
          </Box>
          <Box {...groupBoxSx}>
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Change Control:
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              sx={{ flex: 1 }}
              value={
                policyData.policyChangeControlXrefDto?.changeControlText || ""
              }
              onChange={handleChangeControlChange}
            />
          </Box>
        </Box>
        {/* Center Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Translation Status:</Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyData.translationStatus}
            </Typography>
          </Box>
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Policy Translator:</Typography>
            <PolicyTranslationUserSelect
              sx={{ flex: 1 }}
              users={policyTranslators}
              selectedUser={policyData.policyTranslator}
              setSelectedUser={handleTranslatorChange}
            ></PolicyTranslationUserSelect>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5, pt: "8px" }}
            >
              Translation Reviewer:
            </Typography>
            <FormControl
              fullWidth
              variant="outlined"
              error={showTranslationReviewerError}
              sx={{ flex: 1 }}
            >
              <Select
                value={policyData.translationReviewer}
                onChange={handleReviewerChange}
              >
                {translationReviewers.map((reviewer) => (
                  <MenuItem
                    key={reviewer.userKey}
                    value={reviewer.userEmailAddress}
                  >
                    {`${reviewer.userFirstName} ${reviewer.userLastName}`}
                  </MenuItem>
                ))}
              </Select>
              {showTranslationReviewerError && (
                <FormHelperText
                  data-testid="translation-reviewer-required-error"
                  error
                  sx={{ mt: "4px" }}
                >
                  Translation Reviewer Required.
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>

        {/* Right Column */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.1,
            ml: 10,
            mb: 2,
            alignItems: "flex-end",
            textAlign: "right",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <GreyOutButton
              variant="outlined"
              size="small"
              disabled={disableAdoptionDetailsDrawer()}
              onClick={() => handleDrawerButtonClick("adoptionDetails")}
            >
              Adoption Details
            </GreyOutButton>
            <GreyOutButton
              variant="outlined"
              size="small"
              disabled={!hasCtReviewObjects || !canOpenCtReviewDrawer}
              onClick={() => handleDrawerButtonClick("ctReviews")}
            >
              C&T Reviews
            </GreyOutButton>
            <GreyOutButton
              variant="outlined"
              size="small"
              onClick={() => handleDrawerButtonClick("policyHistory")}
            >
              Policy History
            </GreyOutButton>
          </Box>
        </Box>
      </Box>
      {children}
      {/* Use the Drawers component */}
      {activeDrawer && (
        <Drawers
          policyVersion={policyVersion}
          editable={editable}
          activeDrawer={activeDrawer}
          closeDrawer={closeDrawer}
          healthPlanKey={healthPlanKey}
          policyKey={policyKey}
          status={policyData.translationStatus}
          lobAdoptionDecisions={policyData.lobAdoptionDecisionList}
          policyHistoryData={policyHistoryData}
          setPolicyHistoryData={setPolicyHistoryData}
          ctReviewData={ctReviewData}
          reviewData={reviewData}
          setReviewData={setReviewData}
          ctReviewEditable={canEditCtReviewDrawer}
        />
      )}
    </>
  );
};

export default TopPanelComponent;
